import { Box, Stack, Typography } from "@mui/material";
import { useDecideSsoAuthorizationRequestMutation } from "Api/Mutations/Sso/useDecideSsoAuthorizationRequestMutation";
import { useSsoAuthorizationRequestQuery } from "Api/Queries/Sso/useSsoAuthorizationRequestQuery";
import {
  AuthPage,
  AuthPageContent,
} from "Components/Auth/Shared/AuthStyledComponents";
import { UnauthenticatedHeader } from "Components/Layout/UnauthenticatedHeader";
import { UnauthenticatedLayout } from "Components/Layout/UnauthenticatedLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { SsoConsentCard } from "Components/Sso/SsoConsentCard";
import { SsoReauthenticationCard } from "Components/Sso/SsoReauthenticationCard";
import { getSsoErrorResource } from "Components/Sso/ssoResources";
import { type ReactNode, useEffect, useRef } from "react";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";
import { isGuid } from "Utils/StringUtils";

const RESOURCES = Resources.Sso.Consent;
const REQUEST_ID_PARAMETER = "requestId";

/**
 * The web half of "sign in with Mo.one": the provider parked the partner's authorization request and sent the
 * browser here. Signed in (the route wrapper handles the login detour), the person sees what the app asks for
 * and answers; either answer ends on the provider's resume URL, which issues the code or the access_denied.
 * A standing consent is honoured without asking, and an app that demands a fresh login gets one first.
 */
export const SsoAuthorizePage = () => {
  const { t } = useResource();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get(REQUEST_ID_PARAMETER);
  const publicID = requestId && isGuid(requestId) ? requestId : null;

  const { data, isLoading, error } = useSsoAuthorizationRequestQuery(publicID);
  const {
    mutate: decide,
    isPending: isDeciding,
    isError: hasDecisionFailed,
    error: decisionError,
  } = useDecideSsoAuthorizationRequestMutation({
    onSuccess: (result) => window.location.assign(result.resumeUrl),
  });

  const isAutoApproved =
    !!data && data.hasExistingConsent && !data.requiresReauthentication;
  const hasAutoApprovedRef = useRef(false);
  useEffect(() => {
    if (isAutoApproved && !hasAutoApprovedRef.current) {
      hasAutoApprovedRef.current = true;
      decide({ publicID: data.publicID, isApproved: true });
    }
  }, [isAutoApproved, data, decide]);

  if (!publicID) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.MissingRequest)}</ErrorText>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <ErrorText>{t(getSsoErrorResource(error))}</ErrorText>
      </Layout>
    );
  }

  // A failed answer (request expired between load and click, network) is the end of this request; the
  // person starts again from the application, so the error replaces the card and the auto-approve spinner.
  if (hasDecisionFailed) {
    return (
      <Layout>
        <ErrorText>{t(getSsoErrorResource(decisionError))}</ErrorText>
      </Layout>
    );
  }

  if (data.requiresReauthentication) {
    return (
      <Layout>
        <SsoReauthenticationCard appName={data.clientDisplayName} />
      </Layout>
    );
  }

  if (isAutoApproved) {
    return (
      <Layout>
        <Stack alignItems="center" gap={2}>
          <CenteredCircularProgress />
          <Typography>{t(RESOURCES.Redirecting)}</Typography>
        </Stack>
      </Layout>
    );
  }

  return (
    <Layout>
      <SsoConsentCard
        request={data}
        isDeciding={isDeciding}
        onDecide={(isApproved) =>
          decide({ publicID: data.publicID, isApproved })
        }
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  // Not the app's layout: a partner sends people here in the middle of signing in, so no menu around the card.
  return (
    <UnauthenticatedLayout title={t(Resources.Pages.Titles.SsoAuthorize)}>
      {/* The card is shorter than the sign-in form, so the page itself must reach the bottom of the screen. */}
      <Box minHeight="100dvh">
        <AuthPage>
          <AuthPageContent>
            <UnauthenticatedHeader />
            {children}
          </AuthPageContent>
        </AuthPage>
      </Box>
    </UnauthenticatedLayout>
  );
};

export default SsoAuthorizePage;
